/**
 * Snapshot cache: read and replace the single, non-paginated snapshot of a
 * customer's `profile`, `important_notes`, and `vitals_last_30_days`.
 *
 * Unlike the records cache, the snapshot is stored as one unit under its own
 * key, completely independent of any page number. This lets a non-page-1 fetch
 * update records without touching the snapshot, and lets page 1 replace the
 * snapshot wholesale.
 *
 * The stored value wraps the snapshot so it can carry an associated fetch
 * timestamp (mirroring the page-1 fetch time) without changing the
 * `SnapshotPayload` shape that callers consume. `readSnapshot` unwraps the
 * stored value back to a `SnapshotPayload`, and tolerantly accepts a value
 * that was stored as a bare payload as well.
 *
 * Satisfies:
 * - Requirement 8.1: save `profile`, `important_notes`, and
 *   `vitals_last_30_days` keyed by customer id.
 * - Requirement 8.2: store the three fields as a single snapshot, independent
 *   of any page number.
 */

import { readJson, writeJson } from './storage';
import { HEALTH_SNAPSHOT_KEY } from './keys';

/**
 * The single cached snapshot of a customer's non-paginated health data. All
 * three fields hold the raw server payload and are passed through the existing
 * transform pipeline on read elsewhere.
 */
export interface SnapshotPayload {
  profile: unknown;
  important_notes: unknown;
  vitals_last_30_days: unknown;
  /**
   * Raw `resident_visitation` array from `HcGetCustomerInfoById`. Optional
   * because the original design (Req 8.1) enumerated only the three fields
   * above, but resident visitations are non-paginated snapshot data fetched in
   * the same payload (parsed by `parseResidentVisitations`). It is carried here
   * so the visitation optimistic update (Req 9.4) has a slice to mutate; it is
   * left `undefined` when a snapshot is written without visitation data.
   */
  resident_visitation?: unknown;
}

/** The shape persisted under the snapshot key: the payload plus its timestamp. */
interface StoredSnapshot {
  lastFetchTime?: number;
  snapshot: SnapshotPayload;
}

/**
 * Narrow an unknown stored value to a `StoredSnapshot` wrapper (has a nested
 * `snapshot` object).
 */
function isWrapped(value: unknown): value is StoredSnapshot {
  return (
    typeof value === 'object' &&
    value !== null &&
    'snapshot' in value &&
    typeof (value as { snapshot: unknown }).snapshot === 'object' &&
    (value as { snapshot: unknown }).snapshot !== null
  );
}

/**
 * Read the stored snapshot for a customer.
 *
 * @returns the `SnapshotPayload`, or `null` on a cache miss / parse failure.
 */
export function readSnapshot(customerId: string): SnapshotPayload | null {
  const stored = readJson<unknown>(HEALTH_SNAPSHOT_KEY(customerId));
  if (stored === null) {
    return null;
  }
  // Preferred wrapped shape: { lastFetchTime, snapshot }.
  if (isWrapped(stored)) {
    return stored.snapshot;
  }
  // Tolerate a value stored as a bare payload.
  if (typeof stored === 'object') {
    return stored as SnapshotPayload;
  }
  return null;
}

/**
 * Replace the stored snapshot for a customer.
 *
 * The snapshot is persisted independently of any page number; this is invoked
 * on a page-1 fetch to replace the snapshot wholesale.
 */
export function writeSnapshot(customerId: string, snapshot: SnapshotPayload): void {
  const stored: StoredSnapshot = {
    lastFetchTime: Date.now(),
    snapshot,
  };
  writeJson(HEALTH_SNAPSHOT_KEY(customerId), stored);
}
